/**
 * Source:
 * https://github.com/google/canvas-5-polyfill/blob/master/canvas.js#L2021-L2072
 */

function mag(v) {
  return Math.sqrt(Math.pow(v[0], 2) + Math.pow(v[1], 2));
}

function dot(u, v) {
  return u[0] * v[0] + u[1] * v[1];
}

function ratio(u, v) {
  return dot(u, v) / (mag(u) * mag(v));
}

function angle(u, v) {
  var sign = 1.0;
  if (u[0] * v[1] - u[1] * v[0] < 0) {
    sign = -1.0;
  }
  return sign * Math.acos(ratio(u, v));
}

function rotClockwise(v, angle) {
  var cost = Math.cos(angle);
  var sint = Math.sin(angle);
  return [cost * v[0] + sint * v[1], -1 * sint * v[0] + cost * v[1]];
}

function rotCounterClockwise(v, angle) {
  var cost = Math.cos(angle);
  var sint = Math.sin(angle);
  return [cost * v[0] - sint * v[1], sint * v[0] + cost * v[1]];
}

function midPoint(u, v) {
  return [(u[0] - v[0]) / 2.0, (u[1] - v[1]) / 2.0];
}

function meanVec(u, v) {
  return [(u[0] + v[0]) / 2.0, (u[1] + v[1]) / 2.0];
}

function pointMul(u, v) {
  return [u[0] * v[0], u[1] * v[1]];
}

function scale(c, v) {
  return [c * v[0], c * v[1]];
}

function sum(u, v) {
  return [u[0] + v[0], u[1] + v[1]];
}

// Convert an SVG elliptical arc to a series of canvas commands.
//
// x1, x2: start and stop coordinates of the ellipse.
// rx, ry: radii of the ellipse.
// phi: rotation of the ellipse.
// fA: large arc flag.
// fS: sweep flag.
export function ellipseFromEllipticalArc(x1, rx, ry, phi, fA, fS, x2) {
  // Convert from endpoint to center parametrization, as detailed in:
  //   http://www.w3.org/TR/SVG/implnote.html#ArcImplementationNotes
  if (rx == 0 || ry == 0) {
    return [{ action: 'lineTo', args: x2 }];
  }
  var phi = phi * (Math.PI / 180.0);
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  var xPrime = rotClockwise(midPoint(x1, x2), phi); // F.6.5.1
  var xPrime2 = pointMul(xPrime, xPrime);
  var rx2 = Math.pow(rx, 2);
  var ry2 = Math.pow(ry, 2);

  var lambda = Math.sqrt(xPrime2[0] / rx2 + xPrime2[1] / ry2);
  if (lambda > 1) {
    rx *= lambda;
    ry *= lambda;
    rx2 = Math.pow(rx, 2);
    ry2 = Math.pow(ry, 2);
  }

  var factor = Math.sqrt(
    (rx2 * ry2 - rx2 * xPrime2[1] - ry2 * xPrime2[0]) / (rx2 * xPrime2[1] + ry2 * xPrime2[0]),
  );
  if (fA == fS) {
    factor *= -1.0;
  }
  var cPrime = scale(factor, [(rx * xPrime[1]) / ry, (-ry * xPrime[0]) / rx]); // F.6.5.2
  var c = sum(rotCounterClockwise(cPrime, phi), meanVec(x1, x2)); // F.6.5.3
  var x1UnitVector = [(xPrime[0] - cPrime[0]) / rx, (xPrime[1] - cPrime[1]) / ry];
  var x2UnitVector = [(-1.0 * xPrime[0] - cPrime[0]) / rx, (-1.0 * xPrime[1] - cPrime[1]) / ry];
  var theta = angle([1, 0], x1UnitVector); // F.6.5.5
  var deltaTheta = angle(x1UnitVector, x2UnitVector); // F.6.5.6
  var start = theta;
  var end = theta + deltaTheta;

  return [
    { action: 'save', args: [] },
    { action: 'translate', args: [c[0], c[1]] },
    { action: 'rotate', args: [phi] },
    { action: 'scale', args: [rx, ry] },
    { action: 'arc', args: [0, 0, 1, start, end, 1 - fS] },
    { action: 'restore', args: [] },
  ];
}
